// src/services/conocimiento.ts
/**
 * Service para operaciones de Base de Conocimiento.
 */
import { api } from './api';
import type {
  CategoriaConocimiento,
  CategoriaArbol,
  CategoriaCreate,
  CategoriaUpdate,
  Articulo,
  ArticuloCreate,
  ArticuloUpdate,
  ArticuloListItem,
  ArticuloFiltros,
  ArticuloVersion,
  ArticuloVersionDetalle,
} from '@/types';

export const conocimientoService = {
  // =====================
  // CATEGORÍAS
  // =====================
  categorias: {
    list: (incluirInactivas: boolean = false) =>
      api.get<CategoriaConocimiento[]>(
        `/conocimiento/categorias?incluir_inactivas=${incluirInactivas}`
      ),

    getArbol: () => api.get<CategoriaArbol[]>('/conocimiento/categorias/arbol'),

    get: (id: string) =>
      api.get<CategoriaConocimiento>(`/conocimiento/categorias/${id}`),

    create: (data: CategoriaCreate) =>
      api.post<CategoriaConocimiento>('/conocimiento/categorias', data),

    update: (id: string, data: CategoriaUpdate) =>
      api.patch<CategoriaConocimiento>(`/conocimiento/categorias/${id}`, data),

    delete: (id: string) => api.delete<void>(`/conocimiento/categorias/${id}`),
  },

  // =====================
  // ARTÍCULOS CRUD
  // =====================
  articulos: {
    list: (params?: ArticuloFiltros) => {
      const searchParams = new URLSearchParams();
      if (params?.tipo) searchParams.set('tipo', params.tipo);
      if (params?.ambito) searchParams.set('ambito', params.ambito);
      if (params?.categoria_id) searchParams.set('categoria_id', params.categoria_id);
      if (params?.estado) searchParams.set('estado', params.estado);
      if (params?.skip !== undefined) searchParams.set('skip', params.skip.toString());
      if (params?.limit !== undefined) searchParams.set('limit', params.limit.toString());

      const query = searchParams.toString();
      return api.get<ArticuloListItem[]>(`/conocimiento/articulos${query ? `?${query}` : ''}`);
    },

    get: (id: string) => api.get<Articulo>(`/conocimiento/articulos/${id}`),

    getByNumero: (numero: number) =>
      api.get<Articulo>(`/conocimiento/articulos/numero/${numero}`),

    create: (data: ArticuloCreate) =>
      api.post<Articulo>('/conocimiento/articulos', data),

    update: (id: string, data: ArticuloUpdate) =>
      api.patch<Articulo>(`/conocimiento/articulos/${id}`, data),

    delete: (id: string) => api.delete<void>(`/conocimiento/articulos/${id}`),
  },

  // =====================
  // ARTÍCULOS ACCIONES
  // =====================
  acciones: {
    publicar: (id: string) =>
      api.post<Articulo>(`/conocimiento/articulos/${id}/publicar`),

    archivar: (id: string) =>
      api.post<Articulo>(`/conocimiento/articulos/${id}/archivar`),

    despublicar: (id: string) =>
      api.post<Articulo>(`/conocimiento/articulos/${id}/despublicar`),

    marcarUtil: (id: string, util: boolean = true) =>
      api.post<{ message: string }>(`/conocimiento/articulos/${id}/util?util=${util}`),
  },

  // =====================
  // VERSIONES
  // =====================
  versiones: {
    list: (articuloId: string) =>
      api.get<ArticuloVersion[]>(`/conocimiento/articulos/${articuloId}/versiones`),

    get: (articuloId: string, version: number) =>
      api.get<ArticuloVersionDetalle>(
        `/conocimiento/articulos/${articuloId}/versiones/${version}`
      ),

    revertir: (articuloId: string, version: number) =>
      api.post<Articulo>(
        `/conocimiento/articulos/${articuloId}/versiones/${version}/revertir`
      ),
  },

  // =====================
  // BÚSQUEDA
  // =====================
  buscar: (q: string, params?: ArticuloFiltros) => {
    const searchParams = new URLSearchParams();
    searchParams.set('q', q);
    if (params?.tipo) searchParams.set('tipo', params.tipo);
    if (params?.ambito) searchParams.set('ambito', params.ambito);
    if (params?.categoria_id) searchParams.set('categoria_id', params.categoria_id);
    if (params?.skip !== undefined) searchParams.set('skip', params.skip.toString());
    if (params?.limit !== undefined) searchParams.set('limit', params.limit.toString());

    return api.get<ArticuloListItem[]>(`/conocimiento/buscar?${searchParams.toString()}`);
  },

  getRelacionados: (articuloId: string, limit: number = 5) =>
    api.get<ArticuloListItem[]>(
      `/conocimiento/articulos/${articuloId}/relacionados?limit=${limit}`
    ),
};
